<template>
  <div class="main">
    <Spinner class="is-size-3" v-if="isLoading" />

    <div class="notification is-danger" v-if="userLoadError">{{ userLoadError }}</div>

    <div class="container" v-if="user">
      <!-- Top card -->
      <div class="card">
        <!-- Top card header -->
        <header class="card-header">
          <div class="card-header-title">
            <div class="media">
              <div class="media-content">
                <p class="title is-4">
                  {{ user.firstName }} {{ user.lastName }}
                  <span class="tag is-rounded is-success" v-if="userIsPro">SUBSCRIBER</span>
                </p>
              </div>
            </div>
          </div>
        </header>

        <!-- Top card content -->
        <div class="card-content">
          <div class="content">
            <!-- Top tabs -->
            <div class="tabs is-centered top-tabs">
              <ul>
                <li
                  v-for="tab in topTabs"
                  :key="tab.index"
                  :class="{ 'is-active': tab.index === selectedTopTabIndex }"
                  @click="selectTopTab(tab)"
                >
                  <a>{{ tab.name }}</a>
                </li>
              </ul>
            </div>

            <!-- Top tab content -->
            <component
              :is="selectedTopTabComponent"
              :userStorePath="userStorePath"
              :usersCacheStorePath="usersCacheStorePath"
            ></component>
          </div>
        </div>

        <!-- Footer with buttons -->
        <footer class="card-footer">
          <a class="card-footer-item" @click="changePassword">Change Password</a>
          <a class="card-footer-item" @click="changeEmail">Change Email</a>
        </footer>
      </div>

      <!-- Bottom tabs -->
      <div class="tabs is-toggle is-fullwidth bottom-tabs">
        <ul>
          <li
            v-for="tab in bottomTabs"
            :key="tab.index"
            :class="{ 'is-active': tab.index === selectedBottomTabIndex }"
            @click="selectBottomTab(tab)"
          >
            <a>{{ tab.name }}</a>
          </li>
        </ul>
      </div>

      <!-- Bottom tab content -->
      <div class="tabs-content">
        <component
          :is="selectedBottomTabComponent"
          :user="user"
          :userStorePath="userStorePath"
          :selectedHuntAreaStorePath="selectedHuntAreaStorePath"
          :selectedHuntStorePath="selectedHuntStorePath"
          :selectedGPSStorePath="selectedGPSStorePath"
          :usersCacheStorePath="usersCacheStorePath"
          :huntAreasCacheStorePath="huntAreasCacheStorePath"
        ></component>
      </div>

      <!-- Modals -->
      <UserChangePasswordModal
        v-if="showChangePasswordModal"
        :userStorePath="userStorePath"
        @cancelled="closeChangePasswordModal"
        @succeeded="closeChangePasswordModal"
      />

      <UserChangeEmailModal
        v-if="showChangeEmailModal"
        :userStorePath="userStorePath"
        @cancelled="closeChangeEmailChangeModal"
        @succeeded="closeChangeEmailChangeModal"
      />
    </div>
  </div>
</template>

<script>
import Spinner from '@/components/utils/Spinner'
import UserInfoTab from './UserInfoTab'
import UserProTab from './UserProTab'
import UserMapTilesTab from './UserMapTilesTab'
import UserChangePasswordModal from './UserChangePasswordModal'
import UserChangeEmailModal from './UserChangeEmailModal'
import UserHuntAreasTab from './UserHuntAreasTab'
import UserHuntsTab from './UserHuntsTab'
import UserGPSDevicesTab from './UserGPSDevicesTab'
import UserDogProfilesTab from './UserDogProfilesTab'
import UserActivityLogTab from './UserActivityLogTab'
import UserSubscriptionsTab from './UserSubscriptionsTab'
import UserNotesTab from './UserNotesTab'

import { IS_LOADING_USER, GET_USER, GET_USER_LOAD_ERROR } from './userStore'

import { ADD_USER } from './usersCacheStore'

import { isPro } from './userUtils'

export default {
  name: 'User',

  components: {
    Spinner,
    UserInfoTab,
    UserProTab,
    UserMapTilesTab,
    UserChangePasswordModal,
    UserChangeEmailModal,
    UserHuntAreasTab,
    UserHuntsTab,
    UserGPSDevicesTab,
    UserDogProfilesTab,
    UserActivityLogTab,
    UserSubscriptionsTab,
    UserNotesTab
  },

  props: {
    userStorePath: {
      type: String,
      required: true
    },

    selectedHuntAreaStorePath: {
      type: String,
      required: true
    },

    selectedHuntStorePath: {
      type: String,
      required: true
    },

    selectedGPSStorePath: {
      type: String,
      required: true
    },

    usersCacheStorePath: {
      type: String,
      required: true
    },

    huntAreasCacheStorePath: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      topTabs: [
        {
          index: 0,
          name: 'Info',
          component: UserInfoTab
        },
        {
          index: 1,
          name: 'Pro',
          component: UserProTab
        },
        {
          index: 2,
          name: 'Map Tiles',
          component: UserMapTilesTab
        },
        {
          index: 3,
          name: 'Notes',
          component: UserNotesTab
        }
      ],
      selectedTopTabIndex: 0,

      bottomTabs: [
        {
          index: 0,
          name: 'Hunt Areas',
          component: UserHuntAreasTab
        },
        {
          index: 1,
          name: 'Hunts',
          component: UserHuntsTab
        },
        {
          index: 2,
          name: 'GPS',
          component: UserGPSDevicesTab
        },
        {
          index: 3,
          name: 'Dog Profiles',
          component: UserDogProfilesTab
        },
        {
          index: 4,
          name: 'Activity Log',
          component: UserActivityLogTab
        },
        {
          index: 5,
          name: 'Subscriptions',
          component: UserSubscriptionsTab
        }
      ],
      selectedBottomTabIndex: null,

      showChangePasswordModal: false,
      showChangeEmailModal: false
    }
  },

  methods: {
    selectTopTab(tab) {
      this.selectedTopTabIndex = tab.index
    },

    selectBottomTab(tab) {
      this.selectedBottomTabIndex = tab.index
    },

    changePassword() {
      this.showChangePasswordModal = true
    },

    closeChangePasswordModal() {
      this.showChangePasswordModal = false
    },

    changeEmail() {
      this.showChangeEmailModal = true
    },

    closeChangeEmailChangeModal() {
      this.showChangeEmailModal = false
    }
  },

  computed: {
    userIsPro() {
      return isPro(this.user)
    },

    isLoading() {
      const isLoadingUser = `${this.userStorePath}/${IS_LOADING_USER}`
      return this.$store.getters[isLoadingUser]
    },

    user() {
      const getUser = `${this.userStorePath}/${GET_USER}`
      return this.$store.getters[getUser]
    },

    userLoadError() {
      const getUserLoadError = `${this.userStorePath}/${GET_USER_LOAD_ERROR}`
      return this.$store.getters[getUserLoadError]
    },

    selectedTopTabComponent() {
      return this.topTabs[this.selectedTopTabIndex].component
    },

    selectedBottomTabComponent() {
      if (this.selectedBottomTabIndex === null) {
        return null
      }

      return this.bottomTabs[this.selectedBottomTabIndex].component
    }
  },

  watch: {
    user() {
      // Make sure all modals are closed when the user changes.
      // This may happen, for example, when the user is reloaded after the email was changed.
      this.showChangePasswordModal = false
      this.showChangeEmailModal = false

      // Add user to users cache
      const addUserToCacheAction = `${this.usersCacheStorePath}/${ADD_USER}`
      const user = this.user
      this.$store.dispatch(addUserToCacheAction, { user })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/style.scss';

.main {
  overflow: auto;
  padding: 10px;
}

.card-content {
  padding-top: 5px;

  .table {
    th {
      border-bottom: 0;
    }

    td {
      border-bottom: 0;
    }
  }
}

.bottom-tabs.tabs {
  margin-top: $column-gap * 2;

  a {
    padding: 4px 16px;
  }
}

.top-tabs {
  ul {
    margin: 0;
  }
}

.tabs-content {
  overflow: auto;
}
</style>
